import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Branding from './Branding';
import './ParentInformation.css';

/**
 * ParentInformation Component
 * 
 * A dedicated information section for parents considering purchasing the product.
 * Built using the Hook Model principles to create habit-forming product interest:
 * - Trigger: Emotional triggers around education and child success
 * - Action: Simple next steps with clear value proposition
 * - Variable Reward: Showcasing outcomes and social proof
 * - Investment: Ways parents can start engaging even before purchase
 */
const ParentInformation = ({ onActivateVoice }) => {
  // Current section being viewed
  const [activeSection, setActiveSection] = useState('outcomes');
  // Track if the demo request form is shown
  const [showDemoForm, setShowDemoForm] = useState(false);
  // Track email for the form
  const [email, setEmail] = useState('');
  // Track if the form was submitted
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Parent information sections
  const parentSections = {
    outcomes: {
      title: "Learning Outcomes",
      description: "Parents report their children making significant progress with Tappy Tutor. Our data shows students typically improve 1-2 letter grades within 3 months of regular use, while developing stronger critical thinking skills and learning confidence.",
      visualization: <OutcomesVisualization />
    },
    science: {
      title: "The Science Behind Tappy",
      description: "Tappy uses reinforcement learning to adapt to your child's unique learning style. Our adaptive technology observes how your child learns best and automatically adjusts teaching approaches in real-time, similar to how experienced teachers personalize instruction.",
      visualization: <ScienceVisualization />
    },
    privacy: {
      title: "Privacy & Safety",
      description: "We take your child's privacy seriously. Parents receive learning metrics and progress reports, but conversation content remains private between student and tutor. All data is encrypted, and we never sell information to third parties.",
      visualization: <PrivacyVisualization />
    },
    testimonials: {
      title: "Parent Testimonials",
      description: "Join thousands of parents who've seen their children thrive with Tappy Tutor. Our community of parents report increased confidence, better grades, and a renewed interest in learning.",
      visualization: <TestimonialsVisualization />
    },
    dashboard: {
      title: "Parent Dashboard Preview",
      description: "Track your child's learning journey with our comprehensive dashboard. Monitor progress on key concepts, view achievements, and see tutoring session effectiveness - all without invading your child's learning privacy.",
      visualization: <DashboardPreview />
    }
  };

  const section = parentSections[activeSection];

  const handleDemoRequest = (e) => {
    e.preventDefault();
    // Here you would normally send the email to your backend
    console.log('Demo requested with email:', email);
    setFormSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setShowDemoForm(false);
      setFormSubmitted(false);
      setEmail('');
    }, 3000);
  };

  return (
    <div className="parent-information-container">
      {/* Left sidebar navigation */}
      <div className="parent-info-nav">
        <div className="logo-container">
          <img 
            src="/images/TAPPY-logo-color-1.png" 
            alt="Tappy Logo" 
            className="tappy-logo" 
            style={{ 
              height: '50px',
              width: 'auto',
              marginBottom: Branding.spacing.md,
              marginTop: Branding.spacing.sm
            }}
          />
        </div>

        <ul className="section-links">
          {Object.keys(parentSections).map(section => (
            <li key={section}>
              <button 
                className={`section-link ${activeSection === section ? 'active' : ''}`}
                onClick={() => setActiveSection(section)}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        <div className="separator-line" />
        
        {/* Quick pricing link */}
        <div className="pricing-link-container" style={{ marginTop: Branding.spacing.md }}>
          <Link 
            to="/pricing" 
            className="pricing-link"
            style={{
              color: Branding.colors.tealBlue,
              textDecoration: 'none',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            View Pricing & Plans
            <span style={{ marginLeft: '5px' }}>→</span>
          </Link>
        </div>
      </div>

      {/* Main content area */}
      <div className="parent-info-content">
        {/* Low-friction entry: the free guest session IS the parent path.
            Research content supports the decision; it shouldn't gate it. */}
        <div
          className="parent-try-banner"
          style={{
            background: `linear-gradient(135deg, ${Branding.colors.tealBlue}12, ${Branding.colors.vibrantOrange}10)`,
            border: `1px solid ${Branding.colors.tealBlue}30`,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.md,
            marginBottom: Branding.spacing.lg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: Branding.spacing.md,
            flexWrap: 'wrap'
          }}
        >
          <div>
            <strong style={{ color: Branding.colors.deepNavy || Branding.colors.tealBlue, fontSize: '1.05rem' }}>
              Homework help tonight?
            </strong>
            <div style={{ fontSize: '0.9rem', color: Branding.colors.tealBlue, marginTop: '2px' }}>
              Sit down with your kid and try a free voice session right now. No signup, no credit card.
            </div>
          </div>
          <button
            onClick={onActivateVoice}
            style={{
              background: Branding.colors.vibrantOrange,
              color: Branding.colors.pureWhite,
              border: 'none',
              padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
              borderRadius: Branding.borders.radius.md,
              cursor: 'pointer',
              fontWeight: Branding.typography.fontWeight.bold,
              fontSize: '1rem',
              boxShadow: Branding.shadows.sm,
              whiteSpace: 'nowrap'
            }}
          >
            Try a free session →
          </button>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="section-content"
          >
            <h2 className="section-title">{section.title}</h2>
            <div className="visualization-container">
              {section.visualization}
            </div>
            <p className="section-description">{section.description}</p>

            {/* Call to action at bottom of each section — try-first, pricing second */}
            <div className="section-cta">
              <button
                onClick={onActivateVoice}
                className="try-voice-button"
                style={{
                  background: Branding.colors.vibrantOrange,
                  color: Branding.colors.pureWhite,
                  border: 'none',
                  padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                  borderRadius: Branding.borders.radius.md,
                  cursor: 'pointer',
                  fontWeight: Branding.typography.fontWeight.medium,
                  boxShadow: Branding.shadows.sm,
                  marginRight: Branding.spacing.md
                }}
              >
                Try a Free Session
              </button>
              <Link
                to="/pricing"
                className="section-cta-button"
                style={{
                  background: 'transparent',
                  color: Branding.colors.tealBlue,
                  border: `1px solid ${Branding.colors.tealBlue}`,
                  padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                  borderRadius: Branding.borders.radius.md,
                  textDecoration: 'none',
                  display: 'inline-block',
                  fontWeight: Branding.typography.fontWeight.medium
                }}
              >
                View Plans & Pricing
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Demo request form modal */}
        {showDemoForm && (
          <motion.div 
            className="demo-form-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowDemoForm(false)}
          >
            <motion.div 
              className="demo-form-container"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{
                backgroundColor: Branding.colors.pureWhite,
                padding: Branding.spacing.lg,
                borderRadius: Branding.borders.radius.lg,
                width: '90%',
                maxWidth: '500px',
                boxShadow: Branding.shadows.lg
              }}
            >
              <h3 style={{ 
                marginTop: 0, 
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.md
              }}>
                Request a Personal Demo
              </h3>
              
              {!formSubmitted ? (
                <form onSubmit={handleDemoRequest}>
                  <p style={{ 
                    marginBottom: Branding.spacing.md,
                    color: Branding.colors.slateGray
                  }}>
                    Enter your email and we'll reach out to schedule a personal demo of Tappy Tutor for you and your child.
                  </p>
                  
                  <div style={{ marginBottom: Branding.spacing.md }}>
                    <label 
                      htmlFor="parent-email" 
                      style={{ 
                        display: 'block', 
                        marginBottom: Branding.spacing.xs,
                        color: Branding.colors.darkSlate,
                        fontWeight: Branding.typography.fontWeight.medium
                      }}
                    >
                      Your Email
                    </label>
                    <input 
                      type="email" 
                      id="parent-email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: Branding.spacing.sm,
                        borderRadius: Branding.borders.radius.md,
                        border: `1px solid ${Branding.colors.veryLightGray}`,
                        fontSize: '1rem'
                      }}
                      placeholder="you@example.com"
                    />
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginTop: Branding.spacing.lg
                  }}>
                    <button 
                      type="button" 
                      onClick={() => setShowDemoForm(false)}
                      style={{
                        padding: Branding.spacing.sm,
                        borderRadius: Branding.borders.radius.md,
                        border: 'none',
                        backgroundColor: Branding.colors.offWhite,
                        color: Branding.colors.slateGray,
                        cursor: 'pointer',
                        fontWeight: Branding.typography.fontWeight.medium
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      style={{
                        padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                        borderRadius: Branding.borders.radius.md,
                        border: 'none',
                        backgroundColor: Branding.colors.vibrantOrange,
                        color: Branding.colors.pureWhite,
                        cursor: 'pointer',
                        fontWeight: Branding.typography.fontWeight.medium,
                        boxShadow: Branding.shadows.sm
                      }}
                    >
                      Request Demo
                    </button>
                  </div>
                </form>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '3rem', 
                    marginBottom: Branding.spacing.md 
                  }}>
                    ✅
                  </div>
                  <h3 style={{ 
                    color: Branding.colors.darkSlate,
                    marginBottom: Branding.spacing.sm
                  }}>
                    Thank You!
                  </h3>
                  <p style={{ color: Branding.colors.slateGray }}>
                    We've received your request and will contact you shortly to schedule your personalized demo.
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Visualization components
const OutcomesVisualization = () => (
  <svg width="100%" height="280" viewBox="0 0 400 280">
    <text x="200" y="30" textAnchor="middle" fill="#333" fontSize="16" fontWeight="600">Student Improvement with Tappy</text>
    
    {/* Background grid */}
    <line x1="70" y1="220" x2="330" y2="220" stroke="#e5e7eb" strokeWidth="1" />
    <line x1="70" y1="170" x2="330" y2="170" stroke="#e5e7eb" strokeWidth="1" />
    <line x1="70" y1="120" x2="330" y2="120" stroke="#e5e7eb" strokeWidth="1" />
    <line x1="70" y1="70" x2="330" y2="70" stroke="#e5e7eb" strokeWidth="1" />
    
    {/* Axes */}
    <line x1="70" y1="220" x2="70" y2="60" stroke="#333" strokeWidth="2" />
    <line x1="70" y1="220" x2="330" y2="220" stroke="#333" strokeWidth="2" />
    
    {/* Axis labels */}
    <text x="200" y="250" textAnchor="middle" fill="#333" fontSize="12">Months Using Tappy</text>
    <text x="30" y="140" textAnchor="middle" fill="#333" fontSize="12" transform="rotate(-90 30 140)">Grade Level Performance</text>
    
    {/* Month labels */}
    <text x="100" y="235" textAnchor="middle" fill="#333" fontSize="10">1</text>
    <text x="170" y="235" textAnchor="middle" fill="#333" fontSize="10">2</text>
    <text x="240" y="235" textAnchor="middle" fill="#333" fontSize="10">3</text>
    <text x="310" y="235" textAnchor="middle" fill="#333" fontSize="10">4</text>
    
    {/* Grade labels */}
    <text x="60" y="223" textAnchor="end" fill="#333" fontSize="10">C</text>
    <text x="60" y="173" textAnchor="end" fill="#333" fontSize="10">B</text>
    <text x="60" y="123" textAnchor="end" fill="#333" fontSize="10">A</text>
    <text x="60" y="73" textAnchor="end" fill="#333" fontSize="10">A+</text>
    
    {/* Traditional tutoring line */}
    <path 
      d="M70 200 L100 190 L170 180 L240 175 L310 170" 
      stroke="#717786" 
      strokeWidth="2"
      fill="none"
      strokeDasharray="5,5"
    />
    
    {/* Tappy line */}
    <path 
      d="M70 200 L100 170 L170 130 L240 100 L310 80" 
      stroke="#ea6d37" 
      strokeWidth="3"
      fill="none"
    />
    
    {/* Data points for Tappy */}
    <circle cx="70" cy="200" r="4" fill="#ea6d37" />
    <circle cx="100" cy="170" r="4" fill="#ea6d37" />
    <circle cx="170" cy="130" r="4" fill="#ea6d37" />
    <circle cx="240" cy="100" r="4" fill="#ea6d37" />
    <circle cx="310" cy="80" r="4" fill="#ea6d37" />
    
    {/* Legend */}
    <rect x="100" y="40" width="12" height="3" fill="#717786" strokeDasharray="5,5" />
    <text x="117" y="43" textAnchor="start" fill="#333" fontSize="10">Traditional Tutoring</text>
    
    <rect x="230" y="40" width="12" height="3" fill="#ea6d37" />
    <text x="247" y="43" textAnchor="start" fill="#333" fontSize="10">Tappy Tutor</text>
  </svg>
);

const ScienceVisualization = () => (
  <svg width="100%" height="320" viewBox="0 0 400 320" preserveAspectRatio="xMidYMid meet">
    <defs>
      <linearGradient id="brainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b82b8" />
        <stop offset="100%" stopColor="#297a92" />
      </linearGradient>
      <linearGradient id="learningGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ea6d37" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
      
      {/* Arrow marker definition */}
      <marker
        id="arrowhead"
        markerWidth="10"
        markerHeight="7"
        refX="9"
        refY="3.5"
        orient="auto"
      >
        <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
      </marker>
    </defs>
    
    {/* Background for entire visualization */}
    <rect x="0" y="0" width="400" height="280" fill="#f5f9fa" rx="8" />
    
    {/* Title */}
    <text x="200" y="30" textAnchor="middle" fill="#333" fontSize="16" fontWeight="600">Adaptive Learning Technology</text>
    
    {/* Reinforcement Learning Cycle - moved down */}
    <circle cx="200" cy="170" r="90" fill="none" stroke="#e5e7eb" strokeWidth="2" />
    
    {/* Brain icon - moved down */}
    <circle cx="200" cy="80" r="40" fill="url(#brainGradient)" />
    <text x="200" y="84" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">AI Model</text>
    
    {/* Student icon - moved down */}
    <circle cx="110" cy="170" r="40" fill="#4ade80" />
    <text x="110" y="174" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">Student</text>
    
    {/* Learning strategies icon - moved down */}
    <circle cx="200" cy="260" r="40" fill="url(#learningGradient)" />
    <text x="200" y="255" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Learning</text>
    <text x="200" y="270" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Strategies</text>
    
    {/* Personalization icon - moved down */}
    <circle cx="290" cy="170" r="40" fill="#818cf8" />
    <text x="290" y="165" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Style</text>
    <text x="290" y="180" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Adaptation</text>
    
    {/* Arrows connecting the components - moved down */}
    <path d="M170 105 L140 140" stroke="#333" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" />
    <path d="M140 200 L175 235" stroke="#333" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" />
    <path d="M230 240 L260 200" stroke="#333" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" />
    <path d="M260 140 L230 105" stroke="#333" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" />
    
    {/* Labels for arrows with background to ensure visibility - moved down */}
    <rect x="142" y="110" width="50" height="18" rx="5" fill="white" fillOpacity="0.8" />
    <text x="167" y="123" textAnchor="middle" fill="#333" fontSize="11" fontWeight="500">Teaches</text>
    
    <rect x="142" y="210" width="50" height="18" rx="5" fill="white" fillOpacity="0.8" />
    <text x="167" y="223" textAnchor="middle" fill="#333" fontSize="11" fontWeight="500">Observes</text>
    
    <rect x="232" y="210" width="50" height="18" rx="5" fill="white" fillOpacity="0.8" />
    <text x="257" y="223" textAnchor="middle" fill="#333" fontSize="11" fontWeight="500">Adjusts</text>
    
    <rect x="232" y="110" width="50" height="18" rx="5" fill="white" fillOpacity="0.8" />
    <text x="257" y="123" textAnchor="middle" fill="#333" fontSize="11" fontWeight="500">Improves</text>
    
    {/* Bottom message positioned at the very bottom of the container */}
    <rect x="50" y="300" width="300" height="22" rx="5" fill="white" fillOpacity="0.7" />
    <text x="200" y="315" textAnchor="middle" fill="#333" fontSize="12" fontStyle="italic">The more your child uses Tappy, the better it adapts</text>
  </svg>
);

const PrivacyVisualization = () => (
  <div style={{ 
    height: '100%', 
    padding: '10px',
    background: '#f5f9fa',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between'
  }}>
    <h3 style={{ 
      textAlign: 'center', 
      marginBottom: '20px',
      fontSize: '18px',
      fontWeight: '600',
      color: '#333'
    }}>
      Privacy & Safety
    </h3>
    
    {/* Three privacy cards with clear separation */}
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-around', 
      width: '100%',
      marginBottom: '20px'
    }}>
      {/* Child Privacy Card */}
      <div style={{ 
        background: 'white', 
        padding: '15px', 
        borderRadius: '8px',
        width: '28%',
        textAlign: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          background: '#4ade80',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px auto',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '12px'
        }}>
          COPPA
        </div>
        <h4 style={{ margin: '10px 0', color: '#333' }}>Child Privacy</h4>
        <p style={{ 
          fontSize: '12px', 
          color: '#76909f',
          margin: '0'
        }}>
          Protected by law
        </p>
      </div>
      
      {/* Encryption Card */}
      <div style={{ 
        background: 'white', 
        padding: '15px', 
        borderRadius: '8px',
        width: '28%',
        textAlign: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          background: '#297a92',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px auto',
          position: 'relative'
        }}>
          <span style={{
            display: 'inline-block',
            width: '25px',
            height: '20px',
            background: '#1a5566',
            borderRadius: '2px',
            position: 'relative'
          }}>
            <span style={{
              display: 'block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              border: '2px solid white',
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#1a5566'
            }}></span>
            <span style={{
              display: 'block',
              width: '2px',
              height: '10px',
              background: 'white',
              position: 'absolute',
              top: '5px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}></span>
          </span>
        </div>
        <h4 style={{ margin: '10px 0', color: '#333' }}>Encryption</h4>
        <p style={{ 
          fontSize: '12px', 
          color: '#76909f',
          margin: '0'
        }}>
          End-to-end security
        </p>
      </div>
      
      {/* Parent Access Card */}
      <div style={{ 
        background: 'white', 
        padding: '15px', 
        borderRadius: '8px',
        width: '28%',
        textAlign: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          background: '#ea6d37',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px auto'
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            background: 'white',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '5px 0'
          }}>
            <span style={{
              display: 'block',
              width: '20px',
              height: '3px',
              margin: '0 auto',
              background: '#4ade80',
              borderRadius: '1px'
            }}></span>
            <span style={{
              display: 'block',
              width: '20px',
              height: '3px',
              margin: '0 auto',
              background: '#818cf8',
              borderRadius: '1px'
            }}></span>
            <span style={{
              display: 'block',
              width: '20px',
              height: '3px',
              margin: '0 auto',
              background: '#ea6d37',
              borderRadius: '1px'
            }}></span>
          </div>
        </div>
        <h4 style={{ margin: '10px 0', color: '#333' }}>Parent Access</h4>
        <p style={{ 
          fontSize: '12px', 
          color: '#76909f',
          margin: '0'
        }}>
          Progress metrics
        </p>
      </div>
    </div>
    
    {/* Privacy commitments list */}
    <div style={{ 
      background: 'white', 
      padding: '15px 20px', 
      borderRadius: '8px',
      width: '90%',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      border: '1px solid #e5e7eb'
    }}>
      <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Our Privacy Commitments:</h4>
      
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ 
          display: 'inline-block', 
          width: '8px', 
          height: '8px', 
          borderRadius: '50%', 
          background: '#ea6d37', 
          marginRight: '10px' 
        }}></span>
        <span style={{ fontSize: '14px', color: '#333' }}>Student conversations remain private</span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ 
          display: 'inline-block', 
          width: '8px', 
          height: '8px', 
          borderRadius: '50%', 
          background: '#ea6d37', 
          marginRight: '10px' 
        }}></span>
        <span style={{ fontSize: '14px', color: '#333' }}>Parents see learning progress only</span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ 
          display: 'inline-block', 
          width: '8px', 
          height: '8px', 
          borderRadius: '50%', 
          background: '#ea6d37', 
          marginRight: '10px' 
        }}></span>
        <span style={{ fontSize: '14px', color: '#333' }}>No data selling to third parties</span>
      </div>
    </div>
  </div>
);

const TestimonialsVisualization = () => (
  <div style={{ height: '100%', padding: '10px', overflowY: 'auto' }}>
    {/* Parent testimonial cards */}
    <div style={{ 
      backgroundColor: '#f9fafb', 
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '15px',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          borderRadius: '50%', 
          backgroundColor: '#3b82b8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          marginRight: '10px'
        }}>
          JM
        </div>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Jennifer M.</div>
          <div style={{ fontSize: '12px', color: '#76909f' }}>Parent of 8th grader</div>
        </div>
        <div style={{ marginLeft: 'auto', color: '#f59e0b' }}>
          ★★★★★
        </div>
      </div>
      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
        "My son struggled with math anxiety for years. After 2 months with Tappy, he's not only improved his grade from a C to a B+, but he actually enjoys math now. The way Tappy adapts to his learning style made all the difference."
      </p>
    </div>
    
    <div style={{ 
      backgroundColor: '#f9fafb', 
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '15px',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          borderRadius: '50%', 
          backgroundColor: '#4ade80',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          marginRight: '10px'
        }}>
          DT
        </div>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>David T.</div>
          <div style={{ fontSize: '12px', color: '#76909f' }}>Parent of 10th grader</div>
        </div>
        <div style={{ marginLeft: 'auto', color: '#f59e0b' }}>
          ★★★★★
        </div>
      </div>
      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
        "As a working parent, I don't have time to help with homework every night. Tappy has been a lifesaver. My daughter's confidence has soared, and I love being able to check her progress in the parent dashboard without hovering over her."
      </p>
    </div>
    
    <div style={{ 
      backgroundColor: '#f9fafb', 
      padding: '15px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          borderRadius: '50%', 
          backgroundColor: '#ea6d37',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          marginRight: '10px'
        }}>
          LK
        </div>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Lisa K.</div>
          <div style={{ fontSize: '12px', color: '#76909f' }}>Parent of 6th grader</div>
        </div>
        <div style={{ marginLeft: 'auto', color: '#f59e0b' }}>
          ★★★★★
        </div>
      </div>
      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
        "We tried three different tutors before Tappy. The difference is incredible - my child connects with Tappy in a way she never did with human tutors. Her science grade improved from a C- to an A in one semester. Worth every penny of the Premium subscription."
      </p>
    </div>
  </div>
);

const DashboardPreview = () => (
  <div style={{ 
    height: '100%', 
    backgroundColor: '#f9fafb', 
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  }}>
    {/* Dashboard header */}
    <div style={{ 
      backgroundColor: '#ffffff', 
      padding: '15px 20px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <h3 style={{ margin: 0, fontSize: '16px', color: '#3d4954' }}>
        Parent Dashboard
      </h3>
      <div style={{ 
        backgroundColor: '#ea6d37',
        color: 'white',
        fontSize: '12px',
        padding: '3px 8px',
        borderRadius: '4px'
      }}>
        Premium Feature
      </div>
    </div>
    
    {/* Dashboard content preview */}
    <div style={{ padding: '15px 20px' }}>
      <div style={{ marginBottom: '25px' }}>
        <h4 style={{ fontSize: '14px', margin: '0 0 10px 0', color: '#3d4954' }}>
          Learning Progress
        </h4>
        
        {/* Progress bars */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ fontSize: '12px', color: '#76909f' }}>Algebra</span>
            <span style={{ fontSize: '12px', color: '#3d4954' }}>78%</span>
          </div>
          <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '78%', backgroundColor: '#4ade80', borderRadius: '4px' }}></div>
          </div>
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ fontSize: '12px', color: '#76909f' }}>Earth Science</span>
            <span style={{ fontSize: '12px', color: '#3d4954' }}>62%</span>
          </div>
          <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '62%', backgroundColor: '#3b82b8', borderRadius: '4px' }}></div>
          </div>
        </div>
        
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ fontSize: '12px', color: '#76909f' }}>Grammar</span>
            <span style={{ fontSize: '12px', color: '#3d4954' }}>91%</span>
          </div>
          <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '91%', backgroundColor: '#818cf8', borderRadius: '4px' }}></div>
          </div>
        </div>
      </div>
      
      {/* Recent achievements */}
      <div>
        <h4 style={{ fontSize: '14px', margin: '0 0 10px 0', color: '#3d4954' }}>
          Recent Achievements
        </h4>
        
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#ffffff', 
          borderRadius: '6px',
          marginBottom: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', marginRight: '8px' }}>🏆</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 'medium', color: '#3d4954' }}>Algebra Master</div>
              <div style={{ fontSize: '11px', color: '#76909f' }}>Completed all quadratic equation challenges</div>
            </div>
          </div>
        </div>
        
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#ffffff', 
          borderRadius: '6px',
          marginBottom: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', marginRight: '8px' }}>🔥</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 'medium', color: '#3d4954' }}>5-Day Streak</div>
              <div style={{ fontSize: '11px', color: '#76909f' }}>Consistent learning five days in a row</div>
            </div>
          </div>
        </div>
        
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#ffffff', 
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '16px', marginRight: '8px' }}>💡</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 'medium', color: '#3d4954' }}>Critical Thinker</div>
              <div style={{ fontSize: '11px', color: '#76909f' }}>Applied concepts to solve complex problems</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ParentInformation;